/* Custom dropdown for the YMM search bar and the PDP vehicle selects
   (Asana style-updates item 5).

   The native <select> is kept in the DOM and stays the visible, already-styled
   CLOSED trigger. This script only suppresses the browser's native option list
   and shows a styled panel instead. Choosing an option sets the native
   select's value and fires a real `change` event, so all existing EasySearch /
   fitment logic keeps working unchanged.

   Selects are matched at interaction time (event delegation), so dynamically
   re-rendered selects (EasySearch repopulating Make/Model, the fitment stepper
   swapping the picker markup) are handled automatically. */
(function () {
  'use strict';
  if (window.__customSelectBound) return;
  window.__customSelectBound = true;

  var panel = null;
  var currentSelect = null;
  var activeIndex = -1;
  var lastTouchAt = 0; // debounce mouse events synthesized after a touch

  // A select is enhanced when it is a fitment select or lives inside the YMM widget.
  function isEnhanced(select) {
    if (!select || select.tagName !== 'SELECT') return false;
    if (select.disabled) return false;
    if (select.multiple) return false;
    return select.matches('.fitment-select') || !!select.closest('.easysearch-custom-block');
  }

  // Resolve the enhanced select an event belongs to. Besides the select itself
  // this covers its <label for="…">: the YMM widget renders a floating label
  // INSIDE the field, so a tap on it activates the select and iOS opens its own
  // picker without our handlers ever seeing a select target — one of the ways the
  // native dropdown still slipped through (client feedback round 2, item 6).
  // On touch devices the select itself is `pointer-events: none` (see
  // custom-select.css), so the browser can never open its native picker — but it
  // also means the event target is whatever sits behind the field. Hit-test the
  // enhanced selects by coordinates rather than guessing wrapper class names:
  // the PDP wraps its selects in an accordion body that also holds the Continue
  // button, so a container match would swallow taps meant for that button.
  function enhancedAtPoint(target, x, y) {
    if (!target || typeof x !== 'number' || typeof y !== 'number') return null;
    var sels = document.querySelectorAll('select.fitment-select, .easysearch-custom-block select');
    for (var i = 0; i < sels.length; i++) {
      var s = sels[i];
      if (!isEnhanced(s)) continue;
      // Only accept a select the press actually fell THROUGH to. When the select
      // is `pointer-events: none` the browser hands the event to the box BEHIND
      // it, which is one of its own ancestors. Anything else that merely overlaps
      // the field — the "Shop by Category" menu opening over the YMM bar — is not
      // an ancestor, and must not open the list.
      if (!target.contains(s)) continue;
      var r = s.getBoundingClientRect();
      if (r.width && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return s;
    }
    return null;
  }

  function enhancedFromTarget(target, x, y) {
    if (!target || !target.closest) return null;
    var select = target.closest('select');
    if (!select) {
      var label = target.closest('label[for]');
      if (label) select = document.getElementById(label.getAttribute('for'));
    }
    if (isEnhanced(select)) return select;
    return enhancedAtPoint(target, x, y);
  }

  function ensurePanel() {
    if (panel) return panel;
    panel = document.createElement('ul');
    panel.className = 'cs-panel';
    panel.setAttribute('role', 'listbox');
    document.body.appendChild(panel);

    panel.addEventListener('mousedown', function (e) {
      // Keep focus on the trigger; prevent the outside-close handler firing.
      e.preventDefault();
    });
    panel.addEventListener('click', function (e) {
      // Ignore the ghost click that follows a touch — touch is handled in touchend.
      if (Date.now() - lastTouchAt < 700) return;
      var li = e.target.closest('.cs-option');
      if (!li || li.classList.contains('is-disabled')) return;
      selectValue(li.getAttribute('data-value'));
    });
    // Keep the wheel scroll inside the panel: when there is nothing to scroll,
    // or when scrolling past the top/bottom edge, block the default so it can't
    // rubber-band (elastic bounce showing a gap) or chain to the page (which
    // would scroll it away and close the panel).
    panel.addEventListener(
      'wheel',
      function (e) {
        if (panel.scrollHeight <= panel.clientHeight) {
          e.preventDefault();
          return;
        }
        var atTop = panel.scrollTop <= 0;
        var atBottom = panel.scrollTop + panel.clientHeight >= panel.scrollHeight - 1;
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
          e.preventDefault();
        }
      },
      { passive: false }
    );
    panel.addEventListener(
      'touchmove',
      function (e) {
        if (panel.scrollHeight <= panel.clientHeight) e.preventDefault();
      },
      { passive: false }
    );
    return panel;
  }

  function buildOptions(select) {
    var html = '';
    var opts = select.options;
    // In the YMM bar the placeholder ("Year"/"Make"/"Model") is shown as a greyed
    // out, non-selectable first row, matching the client's WeatherTech reference
    // (client feedback round 2, item 7). PDP selects keep their placeholder
    // hidden — the client asked to leave the product page alone.
    var showPlaceholder = !!select.closest('.easysearch-custom-block');
    for (var i = 0; i < opts.length; i++) {
      var opt = opts[i];
      var isPlaceholder = opt.value === '';
      if (isPlaceholder && !showPlaceholder) continue;
      // The placeholder row is shown but never selectable or highlighted.
      var disabled = isPlaceholder || opt.disabled || opt.classList.contains('disabled');
      var selected = !isPlaceholder && opt.value === select.value;
      var cls = 'cs-option';
      if (disabled) cls += ' is-disabled';
      if (isPlaceholder) cls += ' is-placeholder';
      if (selected) cls += ' is-selected';
      html +=
        '<li class="' + cls + '" role="option"' +
        (selected ? ' aria-selected="true"' : '') +
        (disabled ? ' aria-disabled="true"' : '') +
        ' data-value="' + escapeAttr(opt.value) + '">' +
        escapeHtml((opt.textContent || '').trim()) +
        '</li>';
    }
    return html;
  }

  function escapeHtml(s) {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  var PANEL_MARGIN = 8;
  var MIN_ROOM_BELOW = 330; // enough for a full-height list under the trigger

  // The panel always opens DOWNWARDS and always at full height (client feedback
  // 2026-07-28: a squashed list near the bottom edge doesn't tell the shopper
  // there is more to scroll). When there isn't room, the page is scrolled so the
  // trigger moves into the upper third — SMOOTHLY, and the panel is only anchored
  // and shown once the scroll has come to rest, so nothing jumps or drifts.
  var suppressScrollCloseUntil = 0;
  var openToken = 0;

  function roomBelow(select) {
    return window.innerHeight - select.getBoundingClientRect().bottom - PANEL_MARGIN;
  }

  // Scrolls the trigger up when the list wouldn't fit below it. The panel is
  // already open by now and stays glued to the trigger for the duration of the
  // animation, so the list and the page move together instead of the shopper
  // waiting for the scroll to finish. Following the trigger is safe here because
  // it is bounded to OUR animation — a free page scroll still closes the panel.
  function makeRoomBelow(select) {
    if (roomBelow(select) >= MIN_ROOM_BELOW) return;
    var delta = Math.round(select.getBoundingClientRect().top - window.innerHeight / 3);
    if (delta <= 0) return; // already as high as scrolling can put it

    var token = ++openToken;
    suppressScrollCloseUntil = Date.now() + 2500; // the whole animation
    window.scrollTo({ top: window.scrollY + delta, left: 0, behavior: 'smooth' });

    // No portable `scrollend` yet, so settle is "the position stopped changing",
    // with a hard cap in case it never does.
    var started = Date.now();
    var last = null;
    var still = 0;
    (function follow() {
      if (token !== openToken || currentSelect !== select) return; // closed/superseded
      position(select); // re-anchor + grow the list as room opens up
      var elapsed = Date.now() - started;
      if (window.scrollY === last) still++;
      else {
        still = 0;
        last = window.scrollY;
      }
      if ((elapsed > 100 && still >= 3) || elapsed > 1200) {
        suppressScrollCloseUntil = Date.now() + 150; // swallow the trailing event
        position(select);
        return;
      }
      window.requestAnimationFrame(follow);
    })();
  }

  function position(select) {
    var r = select.getBoundingClientRect();
    var p = ensurePanel();
    var vh = window.innerHeight;
    var spaceBelow = vh - r.bottom - PANEL_MARGIN;
    // Tidy cap so a long list (e.g. 70 years) never fills the whole screen, but
    // never taller than the space actually available so it can't run off-screen.
    var desired = Math.min(320, Math.round(vh * 0.6));

    p.style.minWidth = r.width + 'px';
    p.style.width = r.width + 'px';
    p.style.left = r.left + 'px';
    p.classList.add('is-open');

    p.style.top = r.bottom + 'px';
    p.style.bottom = 'auto';
    p.style.maxHeight = Math.min(desired, spaceBelow) + 'px';
  }

  function openFor(select) {
    var p = ensurePanel();
    currentSelect = select;
    p.style.fontFamily = getComputedStyle(select).fontFamily;
    p.innerHTML = buildOptions(select);
    activeIndex = indexOfSelected();
    // Show the list straight away, then make room for it — the two happen
    // together rather than one after the other.
    position(select);
    highlight(activeIndex);
    if (activeIndex >= 0) {
      scrollActiveIntoView();
    } else {
      p.scrollTop = 0; // fresh open starts at the top of the list
    }
    makeRoomBelow(select);
  }

  function close() {
    if (!panel) return;
    panel.classList.remove('is-open');
    currentSelect = null;
    activeIndex = -1;
    typeBuffer = '';
    openToken++; // abandon a scroll that is still settling
  }

  function isOpen() {
    return panel && panel.classList.contains('is-open');
  }

  function selectValue(value) {
    if (!currentSelect) return;
    var select = currentSelect;
    if (select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    close();
    // Don't refocus the <select> right after a touch — on iOS focusing it
    // reopens the native picker. Mouse/keyboard users keep focus for a11y.
    if (Date.now() - lastTouchAt > 700) {
      try { select.focus({ preventScroll: true }); } catch (e) { select.focus(); }
    }
  }

  function optionEls() {
    return panel ? panel.querySelectorAll('.cs-option') : [];
  }
  function indexOfSelected() {
    var els = optionEls();
    for (var i = 0; i < els.length; i++) {
      if (els[i].classList.contains('is-selected')) return i;
    }
    return -1;
  }
  function highlight(idx) {
    var els = optionEls();
    for (var i = 0; i < els.length; i++) els[i].classList.toggle('is-active', i === idx);
    activeIndex = idx;
  }
  function moveActive(dir) {
    var els = optionEls();
    if (!els.length) return;
    var idx = activeIndex;
    for (var step = 0; step < els.length; step++) {
      idx = (idx + dir + els.length) % els.length;
      if (!els[idx].classList.contains('is-disabled')) break;
    }
    highlight(idx);
    scrollActiveIntoView();
  }
  function scrollActiveIntoView() {
    var els = optionEls();
    if (activeIndex >= 0 && els[activeIndex] && els[activeIndex].scrollIntoView) {
      els[activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  // Type-a-letter jump, like a native <select> and like the client's WeatherTech
  // reference (client feedback round 2, item 3): typing moves the highlight to
  // the first option starting with what was typed and scrolls it into view,
  // WITHOUT selecting it — Enter or a click still commits. Typing the same letter
  // repeatedly cycles through the matches (H → Honda → Hyundai → …).
  var TYPE_RESET_MS = 800;
  var typeBuffer = '';
  var typeBufferAt = 0;

  function isPrintableKey(e) {
    return e.key && e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey;
  }

  function typeahead(ch) {
    var els = optionEls();
    if (!els.length) return;
    var now = Date.now();
    if (now - typeBufferAt > TYPE_RESET_MS) typeBuffer = '';
    typeBufferAt = now;
    typeBuffer += ch;

    // All-same-character buffer ("h", "hh", …) means "cycle on that letter";
    // anything else is a real prefix search from the top of the list.
    var cycling = /^(.)\1*$/.test(typeBuffer);
    var needle = cycling ? typeBuffer.charAt(0) : typeBuffer;
    var from = cycling ? activeIndex + 1 : 0;

    var n = els.length;
    for (var i = 0; i < n; i++) {
      var idx = ((from + i) % n + n) % n;
      var el = els[idx];
      if (el.classList.contains('is-disabled')) continue;
      if (el.textContent.trim().toLowerCase().indexOf(needle) === 0) {
        highlight(idx);
        scrollActiveIntoView();
        return;
      }
    }
  }

  function toggleFor(select) {
    if (isOpen() && currentSelect === select) close();
    else openFor(select);
  }

  // Desktop (mouse): suppress the native dropdown and open our panel.
  document.addEventListener(
    'mousedown',
    function (e) {
      // Ignore the mouse events iOS/Android synthesize right after a touch tap.
      if (Date.now() - lastTouchAt < 700) return;
      var select = enhancedFromTarget(e.target, e.clientX, e.clientY);
      if (!select) return;
      e.preventDefault(); // stop the native desktop dropdown
      toggleFor(select);
      try { select.focus({ preventScroll: true }); } catch (err) { select.focus(); }
    },
    true
  );

  // Touch (iOS/Android): the native picker opens on the tap's default action,
  // which mousedown.preventDefault does NOT cancel — so intercept the touch.
  // Only a real tap (no significant move) opens our panel; scrolls pass through.
  var touchState = null;
  document.addEventListener(
    'touchstart',
    function (e) {
      if (!e.touches[0] || !e.target.closest) {
        touchState = null;
        return;
      }
      var x = e.touches[0].clientX;
      var y = e.touches[0].clientY;
      var select = enhancedFromTarget(e.target, x, y);
      if (select) {
        touchState = { kind: 'select', select: select, x: x, y: y, moved: false };
        return;
      }
      // A touch that begins inside the open panel (to tap OR to scroll the list).
      if (panel && e.target.closest('.cs-panel')) {
        touchState = { kind: 'panel', x: x, y: y, moved: false };
        return;
      }
      touchState = null;
    },
    { passive: true }
  );
  document.addEventListener(
    'touchmove',
    function (e) {
      if (!touchState || !e.touches[0]) return;
      if (
        Math.abs(e.touches[0].clientX - touchState.x) > 8 ||
        Math.abs(e.touches[0].clientY - touchState.y) > 8
      ) {
        touchState.moved = true;
      }
    },
    { passive: true }
  );
  document.addEventListener(
    'touchend',
    function (e) {
      var st = touchState;
      touchState = null;
      if (!st) return;
      // Debounce the mouse/click events iOS synthesizes for THIS gesture,
      // whether it ended as a tap or a scroll (so the ghost click can't select).
      lastTouchAt = Date.now();

      if (st.kind === 'panel') {
        if (st.moved) return; // finger-scrolled the list, not a tap
        // A tap (no movement) on an option — select it.
        var li = e.target.closest && e.target.closest('.cs-option');
        if (!li || li.classList.contains('is-disabled')) return;
        e.preventDefault();
        selectValue(li.getAttribute('data-value'));
      } else {
        // A touch that started on the trigger. ALWAYS cancel the default action,
        // even when the finger drifted past the tap threshold: bailing out early
        // used to let iOS open its own picker on a fast or sloppy tap, which is
        // the ~20% leak the client reported (round 2, item 6). Only the toggle
        // is skipped for a drag.
        e.preventDefault();
        if (!st.moved) toggleFor(st.select);
      }
    },
    { passive: false }
  );

  // Keyboard support on the trigger + inside the open panel.
  document.addEventListener('keydown', function (e) {
    var select = e.target.closest && e.target.closest('select');
    var onEnhanced = select && isEnhanced(select);

    if (isOpen()) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        close();
        if (currentSelect) currentSelect.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveActive(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveActive(-1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        var els = optionEls();
        if (activeIndex >= 0 && els[activeIndex]) {
          selectValue(els[activeIndex].getAttribute('data-value'));
        }
      } else if (e.key === 'Tab') {
        close();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        typeBuffer = '';
      } else if (isPrintableKey(e)) {
        e.preventDefault();
        typeahead(e.key.toLowerCase());
      }
      return;
    }

    if (onEnhanced && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      openFor(select);
    } else if (onEnhanced && isPrintableKey(e)) {
      // Typing on the closed trigger opens our panel and jumps, so the behaviour
      // doesn't depend on whether the list is already open (and the native
      // select's own type-to-select can't change the value behind our back).
      e.preventDefault();
      openFor(select);
      typeahead(e.key.toLowerCase());
    }
  });

  // Close on outside interaction, scroll or resize.
  document.addEventListener(
    'mousedown',
    function (e) {
      if (!isOpen()) return;
      if (panel.contains(e.target)) return;
      if (currentSelect && (e.target === currentSelect || currentSelect.contains(e.target))) return;
      // With `pointer-events: none` on touch devices the event target is the
      // field wrapper, not the select — without this the same press that just
      // opened the panel would be treated as an outside click and close it again.
      if (currentSelect && enhancedFromTarget(e.target, e.clientX, e.clientY) === currentSelect) return;
      close();
    },
    true
  );
  // A real page scroll closes the panel (so it never drifts off its anchor).
  // Scrolling the list itself is ignored, and wheeling/swiping over a short
  // non-scrollable list can't scroll the page (see the wheel/touchmove guard in
  // ensurePanel), so it won't close there either.
  window.addEventListener(
    'scroll',
    function (e) {
      if (!isOpen()) return;
      if (e.target === panel) return;
      // Our own "make room below the trigger" scroll must not close the panel.
      if (Date.now() < suppressScrollCloseUntil) return;
      close();
    },
    true
  );
  window.addEventListener('resize', function () { if (isOpen()) close(); });
})();
